import { FC, MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import styled, { CSSObject } from '@emotion/styled';
import { IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { FaIcon } from '../common/FaIcon';
import { QcsButton } from '../common/basic/QcsButton';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { InputTextWithLang } from '../common/form/InputTextWithLang';
import { QcsTypography } from '../common/basic/QcsTypography';
import { ShowError } from '../ShowError';
import { validations } from '../../utils/validations';
import { Validator } from '../../models/common';
import { fixNameInitValue, getLangNameObj } from '../../utils/format';
import { useAppSelector } from '../../store';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { CertificateTypeDto } from '@api/models/suppliers/CertificateTypeDto';
import { certificateTypeService } from '@api/services/services';
import { resources } from '@api/resources';

const TypesContainer = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    paddingTop: '0.75rem',
  }),
);

const TypeRow = styled.div(
  ({ theme }: any): CSSObject => ({
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.25rem 0.25rem 0.25rem 0.75rem',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  }),
);

const TypeRowActions = styled.div(
  (): CSSObject => ({
    display: 'flex',
    marginLeft: 'auto',
    flexShrink: 0,
  }),
);

const EditorRow = styled.div(
  (): CSSObject => ({
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.25rem',
  }),
);

const EditorActions = styled.div(
  (): CSSObject => ({
    display: 'flex',
    flexShrink: 0,
    marginTop: '0.5rem',
  }),
);

interface DocumentTypeEditorProps {
  type: CertificateTypeDto | null;
  onSubmit: (data: CertificateTypeDto) => Promise<void>;
  onCancel: () => void;
}

const DocumentTypeEditor: FC<DocumentTypeEditorProps> = ({ type, onSubmit, onCancel }) => {
  const { i18n, t } = useTranslation();
  const companyCustomization = useAppSelector(selectCompanyCustomization);

  const validator: Validator = {};

  if (companyCustomization?.applicationLanguages.includes('cs')) {
    validator['name'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('en')) {
    validator['nameEn'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('de')) {
    validator['nameDe'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('pl')) {
    validator['namePl'] = validations.stringRequired(t);
  }
  if (companyCustomization?.applicationLanguages.includes('ru')) {
    validator['nameRu'] = validations.stringRequired(t);
  }

  // No <Form> element here on purpose: the editor lives inside the document form,
  // and a nested <form> would bubble its submit event to the outer one.
  return (
    <FormikDebounce<CertificateTypeDto>
      initialValues={{
        ...type,
        ...fixNameInitValue(i18n, type ?? {}),
      }}
      validationSchema={Yup.object().shape(validator)}
      onSubmit={onSubmit}
    >
      {({ submitForm, isSubmitting }) => (
        <div>
          <EditorRow
            onKeyDown={(e) => {
              // Enter in the name input would implicitly submit the outer document form.
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
                e.preventDefault();
                submitForm();
              }
              if (e.key === 'Escape') {
                e.stopPropagation();
                onCancel();
              }
            }}
          >
            <div style={{ flex: 1 }}>
              <InputTextWithLang isNew={!type} label={t('document.types.name')} required />
            </div>
            <EditorActions>
              <Tooltip title={t('common.save')}>
                <span>
                  <IconButton color="primary" size="small" disabled={isSubmitting} onClick={submitForm}>
                    <CheckIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={t('common.cancel')}>
                <IconButton size="small" onClick={onCancel}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </EditorActions>
          </EditorRow>
          <ShowError resource={[resources.createCertificateType, resources.updateCertificateType]} />
        </div>
      )}
    </FormikDebounce>
  );
};

interface Props {
  certificateId: string;
  initialTypes: CertificateTypeDto[];
  onCountChange?: (count: number) => void;
}

export const DocumentTypes: FC<Props> = ({ certificateId, initialTypes, onCountChange }) => {
  const { i18n, t } = useTranslation();
  const [types, setTypes] = useState<CertificateTypeDto[]>(initialTypes);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);

  const reloadTypes = async () => {
    const res = await certificateTypeService.queryCertificateTypes(certificateId);
    setTypes(res.data);
    onCountChange?.(res.data.length);
  };

  const handleAddClick = () => {
    setAdding(true);
    setEditingId(null);
  };

  const handleEditClick = (type: CertificateTypeDto) => () => {
    setEditingId(type.id!);
    setAdding(false);
  };

  const handleCreate = async (data: CertificateTypeDto) => {
    await certificateTypeService.create(data, { certificateId });
    setAdding(false);
    await reloadTypes();
  };

  const handleUpdate = (id: string) => async (data: CertificateTypeDto) => {
    await certificateTypeService.update(id, data);
    setEditingId(null);
    await reloadTypes();
  };

  const handleDelete = (type: CertificateTypeDto) => async (e: MouseEvent<HTMLElement>) => {
    // The row itself opens the editor on click; don't let delete trigger it too.
    e.stopPropagation();
    if (await certificateTypeService.deleteById(type.id!)) {
      await reloadTypes();
    }
  };

  return (
    <TypesContainer>
      {types.length === 0 && !adding && (
        <QcsTypography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('document.types.empty')}
        </QcsTypography>
      )}

      {types.map((type) =>
        editingId === type.id ? (
          <DocumentTypeEditor key={type.id} type={type} onSubmit={handleUpdate(type.id!)} onCancel={() => setEditingId(null)} />
        ) : (
          <TypeRow key={type.id} onClick={handleEditClick(type)}>
            <QcsTypography variant="body2">{getLangNameObj(i18n, type)}</QcsTypography>
            <TypeRowActions>
              <Tooltip title={t('document.types.edit')}>
                <IconButton size="small" onClick={handleEditClick(type)}>
                  <FaIcon icon={faEdit} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t('common.delete')}>
                <IconButton size="small" color="error" onClick={handleDelete(type)}>
                  <DeleteForeverIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </TypeRowActions>
          </TypeRow>
        ),
      )}

      {adding ? (
        <DocumentTypeEditor type={null} onSubmit={handleCreate} onCancel={() => setAdding(false)} />
      ) : (
        <QcsButton startIcon={<AddIcon />} onClick={handleAddClick} sx={{ alignSelf: 'flex-start' }}>
          {t('document.types.add')}
        </QcsButton>
      )}
    </TypesContainer>
  );
};
